import { Table, Tag, message } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { UseMutateAsyncFunction, useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router";
import { deleteCategory, getCategoryById } from "../../api/category";
import EditButton from "../UI/EditButton/editbutton";
import DeleteButton from "../UI/DeleteButton/deletebutton";
import DeleteModal from "../UI/ModalComponent/DeleteModal/deletemodal";
import ViewButton from "../UI/ViewButton/viewbutton";
import { AxiosResponse } from "axios";
import { getPriceSchemeDropdown } from "../../api/pricescheme";
interface inventoryProps {
  CategoryMutation: UseMutateAsyncFunction<
    AxiosResponse<any, any>,
    unknown,
    {
      limit: number;
      q: string;
      isActive?: boolean | undefined;
    },
    unknown
  >;
  isCategoryLoading: boolean;
  search: string;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  setPaginatedCategory: Dispatch<any>;
  paginatedCategory: any;
  filter: boolean | undefined;
  tableKey: string;
}

interface CategoryListInterface {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
}
const CategoriesList = ({
  CategoryMutation,
  isCategoryLoading,
  search,
  page,
  setPage,
  paginatedCategory,
  setPaginatedCategory,
  filter,
  tableKey,
}: inventoryProps) => {
  const [t] = useTranslation("common");
  const navigate = useNavigate();
  const [, setError] = useState("");
  const [prevPage, setPrevPage] = useState<number>(0);
  const [maxPage, setMaxPage] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | undefined>(undefined);
  const [nameToDelete, setNameToDelete] = useState<any>();
  const [formatedPriceScheme, setFormatedPriceScheme] = useState<{}[]>();

  const { isLoading: loadingPriceScheme } = useQuery(
    "PriceScheme",
    getPriceSchemeDropdown,
    {
      refetchOnWindowFocus: false,
      onSuccess: (res) => {
        setFormatedPriceScheme(
          res.data.map((price: any) => {
            return { value: price.id, label: price.name };
          })
        );
      },
    }
  );

  useEffect(() => {
    page > prevPage &&
      maxPage < page &&
      CategoryMutation({
        limit: page * 10 + 10,
        q: search,
        isActive: filter,
      }).then((res) => {
        if (page > maxPage) setMaxPage(page);

        setPaginatedCategory(res.data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onError = (error: any, context: unknown) => {
    if (setError) {
      setError(error.response.data.message);
      message.error({
        content: error.response.data.message,
        duration: 10,
        onClick: () => {
          message.destroy();
        },
      });
    }
  };

  const deleteCategoryMutation = useMutation("deleteCategory", deleteCategory, {
    onSuccess: () => {
      CategoryMutation({
        limit: page * 10 + 10,
        q: search,
        isActive: filter,
      }).then((res: any) => {
        setPaginatedCategory(res.data);
        message.success("Deleted successfully!");
      });
    },
    onError,
  });

  const DeleteCategory = (id: string | undefined, name: string): void => {
    setOpen(true);
    setIdToDelete(id);
    setNameToDelete(name);
  };

  const handleOk = () => {
    if (idToDelete) {
      deleteCategoryMutation.mutate({ id: idToDelete });
    }
    setOpen(false);
  };

  const getCategoryMutation = useMutation(" getCategory", getCategoryById);

  function fillCategoryById(id: any) {
    getCategoryMutation.mutateAsync(id).then((res) => {
      navigate(`/category/${id}`, {
        state: {
          category: res.data,
          formatedPriceScheme: formatedPriceScheme,
          loadingPriceScheme: loadingPriceScheme,
        },
      });
    });
  }

  const columns: ColumnsType<CategoryListInterface> = [
    {
      title: `${t("order")}`,
      dataIndex: "listOrder",
      width: "5rem",
      key: "listOrder",
      sorter: (a: any, b: any) => a.listOrder - b.listOrder,
      defaultSortOrder: "descend",
    },
    {
      title: `${t("name")}`,
      dataIndex: "name",
      key: "name",
      width: "25rem",
      className: "table-header",
    },
    {
      title: `${t("description")}`,
      dataIndex: "description",
      key: "description",
    },
    {
      title: `${t("priceScheme")}`,
      dataIndex: "price",
      key: "price",
      width: "10rem",
      render: (record: any) => {
        return record ? record.name : null;
      },
    },
    {
      title: `${t("status")}`,
      dataIndex: "isActive",
      align: "center",
      width: "5rem",
      key: "isActive",
      render: (record: any) =>
        record ? (
          <Tag className="active">Active</Tag>
        ) : (
          <Tag className="inactive">Inactive</Tag>
        ),
    },
    {
      title: `${t("action")}`,
      key: "action",
      width: "7rem",
      fixed: "right",
      align: "center",
      render: (record: any) => (
        <>
          <ViewButton
            onClick={(res: any) => {
              navigate(`/items`, {
                state: {
                  q: record.id,
                },
              });
            }}
          />
          <EditButton
            onClick={() => {
              fillCategoryById(record.id);
            }}
          />
          <DeleteButton
            onClick={() => DeleteCategory(record?.id, record?.name)}
          />
        </>
      ),
    },
  ];
  return (
    <>
      <DeleteModal
        open={open}
        setOpen={setOpen}
        name={nameToDelete}
        onOk={handleOk}
      />
      <Table
        key={tableKey}
        locale={{
          triggerDesc: "text",
          triggerAsc: " sort text",
          cancelSort: "cancel text",
        }}
        scroll={{ x: 1000 }}
        columns={columns}
        dataSource={paginatedCategory}
        loading={isCategoryLoading}
        rowClassName={(record, index) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
        rowKey={(record) => record.id}
        onChange={(selectedPage) => {
          setPrevPage(page);
          setPage(selectedPage?.current ?? 1);
        }}
      />
    </>
  );
};

export default CategoriesList;
